/**
 * App-wide React ErrorBoundary.
 *
 * Catches any render-time exception thrown by a child subtree, reports it
 * to Sentry (no-op when Sentry DSN is unset), and shows a fallback UI
 * instead of a white screen.
 *
 * Usage:
 *   <ErrorBoundary>            ← top-level safety net (App.js)
 *     <RestOfApp />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary scope="GamePage">   ← scoped boundary; recovers without
 *     <GamePage />                       a full reload via reset()
 *   </ErrorBoundary>
 *
 * Why class component? Boundaries require `componentDidCatch` /
 * `getDerivedStateFromError` — neither is exposed via hooks. Keeping the
 * boundary deliberately tiny so adding behaviour stays cheap.
 */
import React from "react";
import * as Sentry from "@sentry/react";
import { withTranslation } from "react-i18next";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Sentry init is a no-op when DSN is unset, but the captureException
    // call still works — it just becomes a structured console.error.
    Sentry.captureException(error, {
      tags: { boundary: this.props.scope || "root" },
      extra: { componentStack: info?.componentStack },
    });
    if (process.env.NODE_ENV !== "production") {
      console.error(`[ErrorBoundary:${this.props.scope || "root"}]`, error, info);
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    if (typeof this.props.fallback === "function") {
      return this.props.fallback({ error: this.state.error, reset: this.reset });
    }

    const { t } = this.props;
    return (
      <div
        className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] flex items-center justify-center p-4"
        data-testid="error-boundary-fallback"
      >
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-2 border-[#FF5A5F]/30 shadow-xl rounded-3xl p-6 max-w-md w-full">
          <h1
            className="text-xl font-bold text-[#FF5A5F] mb-2"
            style={{ fontFamily: "Fredoka" }}
          >
            {t("errorBoundary.title")}
          </h1>
          <p className="text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-1">
            {t("errorBoundary.description")}
          </p>
          <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50 mb-4 font-mono break-words">
            {String(this.state.error?.message || this.state.error || t("errorBoundary.unknownError"))}
          </p>
          <div className="flex gap-2">
            <button
              onClick={this.reset}
              className="rounded-xl px-4 py-2 bg-[#38B6FF] text-white font-semibold hover:bg-[#2196F3] transition"
              data-testid="error-boundary-retry"
            >
              {t("errorBoundary.button.retry")}
            </button>
            <button
              onClick={() => window.location.assign("/dashboard")}
              className="rounded-xl px-4 py-2 border-2 border-[#1A237E]/20 dark:border-[#E3F2FD]/20 text-[#1A237E] dark:text-[#E3F2FD] font-semibold hover:bg-[#1A237E]/5 dark:bg-[#E3F2FD]/5 transition"
              data-testid="error-boundary-home"
            >
              {t("errorBoundary.button.home")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation()(ErrorBoundary);
